import { logger } from "@/telemetry";

/**
 * Rows asked for per page when a screen needs the whole listing.
 *
 * This is admin's documented ceiling for its cursor-paged listings
 * (`limit: int = Query(default=100, le=1000)`), so a tenant with fewer rows
 * than this is served in a single request, exactly as before paging existed.
 */
export const FULL_LISTING_PAGE_SIZE = 1000;

/**
 * Hard ceiling on the requests one walk may issue, counting a request that
 * failed and triggered a restart.
 *
 * It exists so that a server bug, for example a cursor that is ignored and
 * the same full page returned forever, cannot loop the browser indefinitely.
 * At the full page size it allows 50,000 rows, far beyond any tenant today.
 */
export const DEFAULT_MAX_PAGES = 50;

/**
 * How many times a walk may start again from the first page after the server
 * rejects its cursor, before the rejection is passed to the caller.
 */
export const MAX_CURSOR_RESTARTS = 3;

/** The paging arguments handed to `fetchPage` for each request. */
export interface PageRequest {
  limit: number;
  /** The id of the last row of the previous page. Absent for the first page. */
  offset?: string;
}

export interface FetchAllPagesOptions<T> {
  /** Rows per request. Defaults to `FULL_LISTING_PAGE_SIZE`. */
  pageSize?: number;
  /** Maximum requests for the whole walk. Defaults to `DEFAULT_MAX_PAGES`. */
  maxPages?: number;
  /** Reads the cursor from a row. Defaults to the row's `id`. */
  getId?: (item: T) => string;
}

export interface FetchAllPagesResult<T> {
  /** Every row, in server order, with any row repeated across pages kept once. */
  items: T[];
  /**
   * True when the walk stopped before the server reported the end, because the
   * page cap was reached or the server repeated a cursor, so rows may be missing.
   */
  truncated: boolean;
}

/** The `detail` admin sends with the 400 for an offset that matches no row. */
const REJECTED_CURSOR_DETAIL = "Unknown offset cursor";

/**
 * True for the 400 admin returns when an `offset` no longer matches a row,
 * typically because the last row of the previous page was deleted in between.
 *
 * Read by duck typing on `statusCode` and `body`, the fields every
 * `WeBuildAiAdminError` carries, in the same manner as `useBuilderAgent`, so
 * this module does not depend on the SDK's error classes. When the raw body is
 * present it must carry admin's cursor detail, so an unrelated 400 such as a
 * validation failure is not retried. An error with no body falls back to the
 * status alone. Exported so that listings which page on demand rather than
 * walking to the end, such as the chat sidebar, apply the same rule.
 */
export const isRejectedCursor = (error: unknown): boolean => {
  const { statusCode, body } = (error as { statusCode?: number; body?: unknown } | null) ?? {};

  if (statusCode !== 400) return false;

  return typeof body !== "string" || body === "" || body.includes(REJECTED_CURSOR_DETAIL);
};

/**
 * The options argument of `fetchAllPages`. A row with a string `id` may omit it,
 * and any other row must pass `getId`, so a caller that forgets it fails to
 * compile rather than collapsing every row under the key "undefined".
 */
export type FetchAllPagesArgs<T> = T extends { id: string }
  ? [options?: FetchAllPagesOptions<T>]
  : [options: FetchAllPagesOptions<T> & { getId: (item: T) => string }];

/** Only reached for a `T` with a string `id`, which `FetchAllPagesArgs` guarantees. */
const defaultGetId = <T>(item: T): string => (item as { id: string }).id;

/**
 * Walks a cursor-paged admin listing until a page shorter than the page size
 * comes back, which is how admin signals the end.
 *
 * Each page after the first is requested with the id of the previous page's
 * last row as its `offset`. A 400 on a page that carried an offset is read as
 * "the cursor is gone" and the walk restarts from the first page, discarding
 * what it had gathered, since the set may have shifted under it. A 400 on the
 * first page, or any other failure, is rethrown so the caller's existing error
 * handling applies unchanged.
 *
 * `fetchPage` receives only the paging arguments, and the caller closes over
 * anything else the endpoint needs (an `orderby`, a language filter). No order
 * is imposed here, so the server's default ordering is preserved.
 */
export async function fetchAllPages<T>(
  fetchPage: (request: PageRequest) => Promise<T[] | null | undefined>,
  ...args: FetchAllPagesArgs<T>
): Promise<FetchAllPagesResult<T>> {
  const options: FetchAllPagesOptions<T> = args[0] ?? {};
  const {
    pageSize = FULL_LISTING_PAGE_SIZE,
    maxPages = DEFAULT_MAX_PAGES,
    getId = defaultGetId,
  } = options;

  let rows = new Map<string, T>();
  let offset: string | undefined;
  let requests = 0;
  let restarts = 0;

  while (requests < maxPages) {
    requests += 1;

    const request: PageRequest =
      offset === undefined ? { limit: pageSize } : { limit: pageSize, offset };
    let page: T[];

    try {
      page = (await fetchPage(request)) ?? [];
    } catch (error) {
      if (offset === undefined || !isRejectedCursor(error) || restarts >= MAX_CURSOR_RESTARTS) {
        throw error;
      }

      restarts += 1;
      logger.warn("Listing cursor was rejected, restarting from the first page", {
        restarts,
      });
      rows = new Map();
      offset = undefined;
      continue;
    }

    for (const item of page) {
      const id = getId(item);

      if (!rows.has(id)) rows.set(id, item);
    }

    if (page.length < pageSize) {
      return { items: [...rows.values()], truncated: false };
    }

    const nextOffset = getId(page[page.length - 1]);

    // A server that ignores the cursor hands back the page it just served, and
    // following it would spend the whole page budget on duplicates.
    if (nextOffset === offset) {
      logger.warn("Listing repeated its cursor, stopping early", { received: rows.size });

      return { items: [...rows.values()], truncated: true };
    }

    offset = nextOffset;
  }

  logger.warn("Listing reached its page cap, some rows may be missing", {
    received: rows.size,
    maxPages,
  });

  return { items: [...rows.values()], truncated: true };
}
